/* ==========================================================================
   Kizmit Designs — inner pages
   Collection grid, collection banner, about page, story sections,
   recommendations, recently viewed and page table of contents.
   Relies on window.Kizmit from theme.js.
   ========================================================================== */
(() => {
  'use strict';
  const K = window.Kizmit;
  if (!K) return;
  const { $, $$, clamp, lerp, reduceMotion, scrollHandlers, tickHandlers, stickyProgress, whenVisible, fresh } = K;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- Collection grid ---------- */
  function setupGrid(sec) {
    const sectionId = sec.dataset.sectionId;
    const drawer = $('[data-filter-drawer]', sec);
    const form = $('[data-filter-form]', sec);

    // Density toggle, remembered per visitor.
    const grid = () => $('[data-grid]', sec);
    const setDensity = (d) => {
      grid()?.setAttribute('data-density', d);
      $$('[data-density]', sec).forEach((b) => { if (b.tagName === 'BUTTON') b.classList.toggle('is-active', b.dataset.density === d); });
      try { localStorage.setItem('kizmit:density', d); } catch (e) { /* ignore */ }
    };
    try { const d = localStorage.getItem('kizmit:density'); if (d) setDensity(d); } catch (e) { /* ignore */ }
    sec.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-density]');
      if (b) setDensity(b.dataset.density);
    });

    // Drawer
    $('[data-filter-open]', sec)?.addEventListener('click', () => drawer?.showModal());
    $('[data-filter-close]', sec)?.addEventListener('click', () => drawer?.close());
    drawer?.addEventListener('click', (e) => { if (e.target === drawer) drawer.close(); });

    // Re-render the section without a full reload.
    async function load(url, push = true) {
      sec.classList.add('is-loading');
      try {
        const u = new URL(url, location.origin);
        u.searchParams.set('section_id', sectionId);
        const html = await (await fetch(u)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cgrid]');
        if (!fresh) { location.href = url; return; }
        drawer?.open && drawer.close();
        $('[data-cgrid-bar]', sec).replaceWith(fresh.querySelector('[data-cgrid-bar]'));
        $('.page-width:not(.cgrid__bar-inner)', sec).replaceWith(fresh.querySelector('.page-width:not(.cgrid__bar-inner)'));
        const d = drawer && fresh.querySelector('[data-filter-drawer] form');
        if (d) $('[data-filter-form]', sec).replaceWith(d);
        if (push) history.pushState({}, '', url);
        try { const saved = localStorage.getItem('kizmit:density'); if (saved) setDensity(saved); } catch (e) { /* ignore */ }
        K.initScope(sec);
        bindMore();
        $('[data-cgrid-bar]', sec).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      } catch (e) {
        location.href = url;
      } finally {
        sec.classList.remove('is-loading');
      }
    }
    function formUrl(f) {
      const params = new URLSearchParams(new FormData(f));
      for (const [k, v] of [...params]) if (v === '') params.delete(k);
      const sort = $('[data-sort]', sec)?.value;
      if (sort) params.set('sort_by', sort);
      return `${f.action}?${params.toString()}`;
    }
    sec.addEventListener('submit', (e) => {
      const f = e.target.closest('[data-filter-form]');
      if (!f) return;
      e.preventDefault();
      load(formUrl(f));
    });
    sec.addEventListener('change', (e) => {
      if (e.target.matches('[data-sort]')) load(formUrl($('[data-filter-form]', sec)));
    });
    sec.addEventListener('click', (e) => {
      const a = e.target.closest('[data-filter-link]');
      if (!a) return;
      e.preventDefault();
      load(a.href);
    });
    window.addEventListener('popstate', () => load(location.href, false));

    // Load more
    function bindMore() {
      const btn = $('[data-more-btn]', sec);
      if (!btn || btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Loading…';
        try {
          const u = new URL(btn.href, location.origin);
          u.searchParams.set('section_id', sectionId);
          const doc = new DOMParser().parseFromString(await (await fetch(u)).text(), 'text/html');
          const items = $$('[data-grid] > li', doc).filter((li) => !li.classList.contains('cgrid__promo'));
          const g = grid();
          items.forEach((li, i) => { li.style.setProperty('--d', `${(i % 4) * 0.07}s`); g.appendChild(li); });
          K.initScope(g);
          const total = Number(btn.dataset.total);
          const seen = g.querySelectorAll('.cgrid__item').length;
          $('[data-seen]', sec).textContent = seen;
          $('[data-seen-bar]', sec).style.setProperty('--p', (seen / total).toFixed(3));
          const next = $('[data-more-btn]', doc);
          if (next) { btn.href = next.getAttribute('href'); btn.textContent = 'Show more pieces'; btn.removeAttribute('aria-busy'); }
          else btn.remove();
        } catch (err) {
          location.href = btn.href;
        }
      });
    }
    bindMore();
  }

  /* ---------- Collection banner ---------- */
  function setupCollectionHero(hero) {
    const more = $('[data-chero-more]', hero);
    more?.addEventListener('click', () => {
      const full = $('.chero__full', hero);
      const short = $('.chero__short', hero);
      const open = full.hidden;
      full.hidden = !open;
      short.hidden = open;
      more.textContent = open ? 'Show less' : 'Read the story';
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    requestAnimationFrame(() => hero.classList.add('is-in'));
    const fan = $('[data-chero-fan]', hero);
    if (!fan || reduceMotion) return;
    scrollHandlers.add(() => {
      const r = hero.getBoundingClientRect();
      const p = clamp(-r.top / r.height, 0, 1);
      fan.style.setProperty('--fan', p.toFixed(3));
    });
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      fan.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
      fan.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
    });
  }

  /* ---------- About banner ---------- */
  function setupAboutHero(hero) {
    requestAnimationFrame(() => hero.classList.add('is-in'));
    if (reduceMotion) return;
    const float = $('[data-ahero-float]', hero);
    let mx = 0; let my = 0; let tx = 0; let ty = 0; let sp = 0;
    hero.addEventListener('pointermove', (e) => {
      mx = e.clientX / window.innerWidth - 0.5;
      my = e.clientY / window.innerHeight - 0.5;
    });
    scrollHandlers.add(() => {
      const r = hero.getBoundingClientRect();
      sp = clamp(-r.top / r.height, 0, 1);
    });
    let visible = true;
    whenVisible(hero, (v) => { visible = v; });
    tickHandlers.add(() => {
      if (!visible) return;
      tx = lerp(tx, mx, 0.06);
      ty = lerp(ty, my, 0.06);
      float.style.setProperty('--mx', tx.toFixed(4));
      float.style.setProperty('--my', ty.toFixed(4));
      float.style.setProperty('--sp', sp.toFixed(4));
    });
  }

  /* ---------- Timeline: big year follows the chapter on screen ---------- */
  function setupTimeline(sec) {
    const years = $$('[data-year]', sec);
    const dots = $$('[data-year-dot]', sec);
    const chapters = $$('[data-chapter]', sec);
    const set = (i) => {
      years.forEach((y, k) => { y.classList.toggle('is-active', k === i); y.classList.toggle('is-past', k < i); });
      dots.forEach((d, k) => d.classList.toggle('is-active', k <= i));
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) set(chapters.indexOf(e.target)); });
    }, { rootMargin: '-45% 0px -45% 0px' });
    chapters.forEach((c) => io.observe(c));
  }

  /* ---------- Statement: words light up with scroll ---------- */
  function setupStatement(sec) {
    const words = $$('.astate__w', sec);
    const track = $('.astate__track', sec);
    if (reduceMotion) { words.forEach((w) => w.classList.add('is-lit')); return; }
    scrollHandlers.add(() => {
      const p = stickyProgress(track);
      const lit = Math.round(p * 1.15 * words.length);
      words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
    });
  }

  /* ---------- Tilt cards ---------- */
  function setupTilt(card) {
    if (reduceMotion) return;
    card.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      card.style.setProperty('--ry', `${((px - 0.5) * 12).toFixed(2)}deg`);
      card.style.setProperty('--rx', `${((0.5 - py) * 10).toFixed(2)}deg`);
      card.style.setProperty('--gx', `${(px * 100).toFixed(1)}%`);
      card.style.setProperty('--gy', `${(py * 100).toFixed(1)}%`);
    });
    card.addEventListener('pointerleave', () => {
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
    });
  }

  /* ---------- Count up numbers ---------- */
  function setupCount(el) {
    const target = Number(el.dataset.countTo);
    if (!Number.isFinite(target) || reduceMotion) return;
    const start = target > 1900 && target < 2100 ? target - 40 : 0;
    el.textContent = start;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const dur = 1600;
      const step = (now) => {
        const p = clamp((now - t0) / dur, 0, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(start + (target - start) * eased);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.6 });
    io.observe(el);
  }

  /* ---------- Craft steps: line draws across ---------- */
  function setupCraft(sec) {
    const line = $('[data-craft-line]', sec);
    if (!line) return;
    if (reduceMotion) { line.style.setProperty('--p', 1); return; }
    scrollHandlers.add(() => {
      const r = sec.getBoundingClientRect();
      const p = clamp((window.innerHeight - r.top) / (window.innerHeight * 0.9), 0, 1);
      line.style.setProperty('--p', p.toFixed(3));
    });
  }

  /* ---------- Closer look: vertical scroll drives a 3D horizontal rail ---------- */
  function setupCloser(sec) {
    const track = $('.closer__track', sec);
    const rail = $('[data-closer-rail]', sec);
    const items = $$('[data-closer-item]', sec);
    const indexEl = $('[data-closer-index]', sec);
    const bar = $('[data-closer-bar]', sec);
    if (reduceMotion) return;
    scrollHandlers.add(() => {
      const p = stickyProgress(track);
      const max = rail.scrollWidth - window.innerWidth;
      const x = -p * Math.max(0, max);
      rail.style.transform = `translate3d(${x.toFixed(1)}px,0,0)`;
      const cx = window.innerWidth / 2;
      let best = 0; let bestD = Infinity;
      items.forEach((it, i) => {
        const r = it.getBoundingClientRect();
        const d = (r.left + r.width / 2 - cx) / window.innerWidth;
        if (Math.abs(d) < bestD) { bestD = Math.abs(d); best = i; }
        it.style.transform = `perspective(1400px) rotateY(${(clamp(d, -1, 1) * -20).toFixed(2)}deg) translateZ(${(-Math.min(1, Math.abs(d)) * 120).toFixed(1)}px) translateY(${(Math.min(1, Math.abs(d)) * 26).toFixed(1)}px)`;
        it.style.opacity = (1 - Math.min(0.5, Math.abs(d) * 0.45)).toFixed(2);
      });
      if (indexEl) indexEl.textContent = String(best + 1).padStart(2, '0');
      bar?.style.setProperty('--p', p.toFixed(3));
    });
  }

  /* ---------- Product recommendations ---------- */
  async function setupRecs(sec) {
    if (sec.children.length || !sec.dataset.url) return;
    try {
      const html = await (await fetch(sec.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const got = doc.querySelector('[data-recs]');
      if (got && got.innerHTML.trim()) {
        sec.innerHTML = got.innerHTML;
        K.initScope(sec);
      }
    } catch (e) { /* recommendations are optional */ }
  }

  /* ---------- Recently viewed ---------- */
  async function setupRecent(sec) {
    let list = [];
    try { list = JSON.parse(localStorage.getItem('kizmit:recent') || '[]'); } catch (e) { return; }
    const current = sec.dataset.current;
    const handles = list.filter((h) => h && h !== current).slice(0, Number(sec.dataset.limit || 6));
    if (!handles.length) return;
    const rail = $('[data-recent-rail]', sec);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const money = (cents) => `₹${(cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    const results = await Promise.all(handles.map(async (h) => {
      try { const r = await fetch(`${root}products/${h}.js`); return r.ok ? r.json() : null; } catch (e) { return null; }
    }));
    const cards = results.filter(Boolean).map((p) => {
      const title = p.title.split(' | ')[0].split(' — ')[0];
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=600` : '';
      const was = p.compare_at_price > p.price ? `<s>${money(p.compare_at_price)}</s>` : '';
      return `<article class="p-card"><div class="p-card__frame"><a href="${esc(p.url)}" class="p-card__media media" tabindex="-1" aria-hidden="true">${img ? `<img src="${esc(img)}" alt="" loading="lazy" class="p-card__img" width="600" height="750">` : ''}</a></div>
        <div class="p-card__info"><h3 class="p-card__title"><a href="${esc(p.url)}">${esc(title)}</a></h3><p class="p-card__sub">${esc(p.type)}</p>
        <div class="p-card__price"><span class="price"><span>${money(p.price)}</span>${was}</span></div></div></article>`;
    });
    if (!cards.length) return;
    rail.innerHTML = cards.join('');
    sec.hidden = false;
  }

  /* ---------- Page table of contents ---------- */
  function setupToc(page) {
    const src = $('[data-toc-source]', page);
    const toc = $('[data-toc]', page);
    const list = $('[data-toc-list]', page);
    const heads = $$('h2, h3', src).filter((h) => h.textContent.trim().length > 1);
    if (heads.length < 3) return;
    heads.forEach((h, i) => {
      if (!h.id) h.id = `section-${i + 1}`;
      const li = document.createElement('li');
      li.innerHTML = `<a href="#${h.id}">${esc(h.textContent.trim())}</a>`;
      if (h.tagName === 'H3') li.classList.add('is-sub');
      list.appendChild(li);
    });
    toc.hidden = false;
    page.classList.add('has-toc');
    const links = $$('a', list);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        links.forEach((a) => a.classList.toggle('is-active', a.getAttribute('href') === `#${e.target.id}`));
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    heads.forEach((h) => io.observe(h));
  }

  function init(scope) {
    fresh('[data-cgrid]', scope).forEach(setupGrid);
    fresh('[data-chero]', scope).forEach(setupCollectionHero);
    fresh('[data-ahero]', scope).forEach(setupAboutHero);
    fresh('[data-timeline]', scope).forEach(setupTimeline);
    fresh('[data-statement]', scope).forEach(setupStatement);
    fresh('[data-tilt-card]', scope).forEach(setupTilt);
    fresh('[data-count-to]', scope).forEach(setupCount);
    fresh('[data-craft]', scope).forEach(setupCraft);
    fresh('[data-closer]', scope).forEach(setupCloser);
    fresh('[data-recs]', scope).forEach(setupRecs);
    fresh('[data-recent]', scope).forEach(setupRecent);
    fresh('[data-mpage]', scope).forEach(setupToc);
    K.frame.force = true;
  }
  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
